import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { StatusBadge, Th, Td, Empty } from "../components/shared";
import { inr, pct, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import ExportMenu from "../components/ExportMenu";
import { toast } from "sonner";
import { Plus, Search, Download, Sparkles, ClipboardList, Loader2, Trash2, Pencil, X, BrainCircuit } from "lucide-react";

const emptyForm = {
  item_name: "", category: "Consumables", branch: "Sarita Vihar", vendor_name: "", unit: "Piece",
  qty: 1, unit_quoted_price: 0, final_unit_price: 0, date_of_request: "", po_date: "",
  expected_delivery: "", actual_delivery: "", payment_status: "Unpaid", payment_date: "",
  order_status: "Draft", remarks: "",
};

export default function Procurement() {
  const { meta, branch } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [negotiation, setNegotiation] = useState("");
  const [negLoading, setNegLoading] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [aiMatches, setAiMatches] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/procurement", { params: { branch, status, search } })
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [branch, status, search]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runNegotiation = async () => {
    if (!form.item_name) { toast.error("Enter item name first"); return; }
    setNegLoading(true);
    try {
      const res = await api.post("/ai/negotiation", { item_name: form.item_name, quoted_price: Number(form.unit_quoted_price) });
      setNegotiation(res.data.answer);
    } catch { setNegotiation("AI unavailable."); }
    finally { setNegLoading(false); }
  };

  const openCreate = () => { setEditId(null); setForm(emptyForm); setNegotiation(""); setOpen(true); };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      item_name: r.item_name || "", category: r.category, branch: r.branch, vendor_name: r.vendor_name || "",
      unit: r.unit || "Piece", qty: r.qty, unit_quoted_price: r.unit_quoted_price, final_unit_price: r.final_unit_price,
      date_of_request: r.date_of_request || "", po_date: r.po_date || "", expected_delivery: r.expected_delivery || "",
      actual_delivery: r.actual_delivery || "", payment_status: r.payment_status, payment_date: r.payment_date || "",
      order_status: r.order_status, remarks: r.remarks || "",
    });
    setNegotiation(""); setOpen(true);
  };

  const submit = async () => {
    if (!form.item_name) { toast.error("Item name required"); return; }
    const payload = { ...form, qty: Number(form.qty), unit_quoted_price: Number(form.unit_quoted_price), final_unit_price: Number(form.final_unit_price) };
    try {
      if (editId) { await api.put(`/procurement/${editId}`, payload); toast.success("Requisition updated"); }
      else { await api.post("/procurement", payload); toast.success("Requisition created"); }
      setOpen(false); setEditId(null); setForm(emptyForm); setNegotiation(""); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    await api.delete(`/procurement/${id}`); toast.success("Deleted"); load();
  };

  const runAiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    try {
      const res = await api.post("/procurement/ai-search", { query: aiQuery.trim() });
      setAiMatches(res.data.pr_ids || []); setAiSummary(res.data.summary || "");
    } catch { toast.error("AI search failed"); }
    finally { setAiLoading(false); }
  };

  const clearAiSearch = () => { setAiMatches(null); setAiSummary(""); setAiQuery(""); };

  const COLS = ["PR ID", "Date", "Item", "Category", "Branch", "Vendor", "Qty", "Quoted", "Final", "Saved", "Save%", "PO Status", "Payment", "Exp. Del", "Delay", "Pay TAT", ""];

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Purchase Requisition & Order Log</h1>
          <p className="text-brand-slate text-sm mt-1">Full PR → PO → delivery → payment lifecycle</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu dataset="procurement" testid="export-procurement" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-requisition-button" onClick={openCreate} className="gap-2 bg-brand-teal hover:bg-brand-tealdark active:scale-95 transition-transform"><Plus className="w-4 h-4" /> New Requisition</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Requisition" : "Create New Requisition"}</DialogTitle>
                <DialogDescription>Capture item, vendor, pricing and delivery details. Savings and delay are auto-calculated.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Item / Product Name</Label>
                  <Input data-testid="req-item-name" value={form.item_name} onChange={(e) => setF("item_name", e.target.value)} placeholder="e.g. HydraFacial Serum Set" /></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setF("category", v)}>
                    <SelectTrigger data-testid="req-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Branch</Label>
                  <Select value={form.branch} onValueChange={(v) => setF("branch", v)}>
                    <SelectTrigger data-testid="req-branch"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Vendor</Label>
                  <Select value={form.vendor_name} onValueChange={(v) => setF("vendor_name", v)}>
                    <SelectTrigger data-testid="req-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>{meta.vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setF("unit", e.target.value)} /></div>
                <div><Label>Quantity</Label><Input data-testid="req-qty" type="number" value={form.qty} onChange={(e) => setF("qty", e.target.value)} /></div>
                <div><Label>Quoted Price / Unit (₹)</Label><Input data-testid="req-quoted" type="number" value={form.unit_quoted_price} onChange={(e) => setF("unit_quoted_price", e.target.value)} /></div>
                <div><Label>Final Price / Unit (₹)</Label><Input data-testid="req-final" type="number" value={form.final_unit_price} onChange={(e) => setF("final_unit_price", e.target.value)} /></div>

                <div className="col-span-2 bg-teal-50 border border-teal-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-brand-teal flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Smart Negotiation Helper</span>
                    <Button data-testid="req-negotiation-btn" type="button" size="sm" variant="outline" onClick={runNegotiation} disabled={negLoading} className="h-7 text-xs border-brand-teal text-brand-teal">
                      {negLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Check history"}
                    </Button>
                  </div>
                  {negotiation && <p className="text-sm text-slate-700 mt-2" data-testid="req-negotiation-result">{negotiation}</p>}
                </div>

                <div><Label>Order Status</Label>
                  <Select value={form.order_status} onValueChange={(v) => setF("order_status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.order_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Payment Status</Label>
                  <Select value={form.payment_status} onValueChange={(v) => setF("payment_status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.payment_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Expected Delivery</Label><Input type="date" value={form.expected_delivery} onChange={(e) => setF("expected_delivery", e.target.value)} /></div>
                <div><Label>PO Date</Label><Input type="date" value={form.po_date} onChange={(e) => setF("po_date", e.target.value)} /></div>
                <div><Label>Actual Delivery</Label><Input type="date" value={form.actual_delivery} onChange={(e) => setF("actual_delivery", e.target.value)} /></div>
                <div><Label>Payment Date</Label><Input type="date" value={form.payment_date} onChange={(e) => setF("payment_date", e.target.value)} /></div>
                <div className="col-span-2"><Label>Remarks</Label><Textarea value={form.remarks} onChange={(e) => setF("remarks", e.target.value)} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="req-submit" onClick={submit} className="bg-brand-teal hover:bg-brand-tealdark">{editId ? "Save Changes" : "Create Requisition"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-gradient-to-r from-navy to-brand-tealdark rounded-xl p-4">
        <div className="flex items-center gap-2 text-white mb-2">
          <BrainCircuit className="w-4 h-4 text-teal-200" />
          <span className="text-sm font-semibold">AI Smart Search</span>
          <span className="text-xs text-teal-200/80 hidden sm:inline">— ask in plain English, e.g. "delayed orders from MedEquip" or "orders above ₹50k not yet paid"</span>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Sparkles className="w-4 h-4 text-teal-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="procurement-ai-search" value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runAiSearch()}
              placeholder="Search procurement records with AI…"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-white/95 border-0 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal-300" />
          </div>
          <Button data-testid="procurement-ai-search-btn" onClick={runAiSearch} disabled={aiLoading} className="h-10 bg-brand-teal hover:bg-brand-emerald gap-2">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
          </Button>
          {aiMatches !== null && <Button variant="outline" onClick={clearAiSearch} className="h-10 bg-white gap-1"><X className="w-4 h-4" /> Clear</Button>}
        </div>
        {aiMatches !== null && <p className="text-teal-100 text-sm mt-2" data-testid="procurement-ai-summary">{aiSummary} <span className="text-teal-300 font-semibold">({aiMatches.length} match{aiMatches.length === 1 ? "" : "es"})</span></p>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="procurement-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item, vendor, branch…" className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="status-filter" className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Statuses</SelectItem>
            {meta.order_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border"><tr>{COLS.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={COLS.length} className="py-16 text-center text-brand-slate">Loading…</td></tr>
                : (aiMatches !== null ? rows.filter((r) => aiMatches.includes(r.pr_id)) : rows).length === 0 ? <tr><td colSpan={COLS.length}><Empty icon={ClipboardList} msg="No purchase orders found" /></td></tr>
                : (aiMatches !== null ? rows.filter((r) => aiMatches.includes(r.pr_id)) : rows).map((r) => (
                  <tr key={r.id} data-testid="procurement-row" className="border-b border-border/60 hover:bg-slate-50 transition-colors">
                    <Td className="font-mono text-xs text-brand-slate">{r.pr_id}</Td>
                    <Td>{fmtDate(r.date_of_request)}</Td>
                    <Td className="font-medium max-w-[220px] truncate">{r.item_name}</Td>
                    <Td className="text-slate-600">{r.category}</Td>
                    <Td className="text-slate-600">{r.branch}</Td>
                    <Td className="text-slate-600">{r.vendor_name || "—"}</Td>
                    <Td>{r.qty} {r.unit}</Td>
                    <Td className="tabular-nums">{inr(r.total_quoted)}</Td>
                    <Td className="tabular-nums font-semibold">{inr(r.total_final)}</Td>
                    <Td className="tabular-nums text-brand-emerald font-semibold">{inr(r.amount_saved)}</Td>
                    <Td className="text-brand-emerald">{pct(r.savings_pct)}</Td>
                    <Td><StatusBadge value={r.order_status} /></Td>
                    <Td><StatusBadge value={r.payment_status} type="payment" /></Td>
                    <Td>{fmtDate(r.expected_delivery)}</Td>
                    <Td className={r.delay_days > 0 ? "text-rose-600 font-semibold" : "text-slate-600"}>{r.delay_days == null ? "—" : `${r.delay_days}d`}</Td>
                    <Td className="text-slate-600">{r.payment_tat == null ? "—" : `${r.payment_tat}d`}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <button data-testid="edit-procurement" onClick={() => openEdit(r)} className="text-slate-400 hover:text-brand-teal"><Pencil className="w-4 h-4" /></button>
                        <button data-testid="delete-procurement" onClick={() => remove(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
